import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { COLLECTION, ARTICLES } from "@/data/brandBranding";
import { SITE } from "@/data/config";
import { Overline, Crumbs, PrimaryButton, SecondaryButton } from "@/components/Primitives";
import CTABlock from "@/components/CTABlock";

const BrandBrandingHub = () => {
  const absUrl = `${SITE.url}/brand-branding`;

  useSeo({
    title: "Brand & Branding · Kostaja",
    description:
      "A connected educational system on brand, branding, identity, strategy, positioning, and visual design. Ten essays that connect what a brand actually is, how brand strategy works, and how visual identity operates as a system.",
    canonical: absUrl,
    ogImage: `${SITE.url}/og/kostaja-og-default.jpg?v=1`,
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "Brand & Branding",
        description: COLLECTION.intro,
        url: absUrl,
        isPartOf: { "@type": "WebSite", url: SITE.url, name: "Kostaja Ltd." },
        hasPart: ARTICLES.map((a) => ({
          "@type": "Article",
          headline: a.title,
          url: `${SITE.url}/brand-branding/${a.slug}`,
          articleSection: a.cluster,
          description: a.metaDescription,
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: SITE.url },
          { "@type": "ListItem", position: 2, name: "Brand & Branding", item: absUrl },
        ],
      },
    ],
  });

  const byCluster = useMemo(() => {
    const map = {};
    for (const c of COLLECTION.clusters) map[c.name] = [];
    for (const a of ARTICLES) map[a.cluster].push(a);
    return map;
  }, []);

  return (
    <main data-testid="brand-branding-hub" className="bg-black text-white">
      {/* HERO */}
      <section className="bg-black border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-12 lg:py-24">
          <Crumbs items={[{ to: "/", label: "Home" }, { label: "Brand & Branding" }]} />
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-8">
              <Overline color="yellow">Collection · 32 essays</Overline>
              <h1 className="mt-3 text-5xl sm:text-6xl lg:text-[92px] font-black tracking-tighter leading-[0.9]">
                Brand <span className="k-yellow">&</span> Branding.
              </h1>
              <p className="mt-6 max-w-2xl text-white/75 text-lg lg:text-xl leading-relaxed">
                A business magazine crossed with a practical textbook. Thirty-two connected essays across six clusters that explain what a brand actually is, how strategy works, how visual identity operates as a system, how a brand sounds and speaks, how it is experienced, and how it is managed over time.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <PrimaryButton to={`/brand-branding/${ARTICLES[0].slug}`} testId="hub-start-reading">
                  Start with essay one
                </PrimaryButton>
                <SecondaryButton href="#clusters" testId="hub-browse-clusters" dark>
                  Browse the collection
                </SecondaryButton>
              </div>
            </div>
            <aside className="lg:col-span-4">
              <div className="border-2 border-[#FFC600] p-6 bg-black">
                <Overline color="yellow">How to read this</Overline>
                <p className="mt-3 text-white/85 text-sm leading-relaxed">
                  Read in order for the fullest picture, or open any essay directly. Each piece stands on its own and links to the next logical concept and to adjacent topics at the bottom.
                </p>
                <ul className="mt-5 space-y-2 text-sm">
                  {COLLECTION.clusters.map((c) => (
                    <li key={c.slug} className="flex items-baseline gap-3">
                      <span className="text-[#FFC600] font-bold text-xs uppercase tracking-[0.2em]">
                        {byCluster[c.name].length}
                      </span>
                      <a href={`#${c.slug}`} className="text-white/85 hover:text-[#FFC600] font-semibold">
                        {c.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* CLUSTERS */}
      <section id="clusters" className="bg-black border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-16 lg:py-24 space-y-16 lg:space-y-24">
          {COLLECTION.clusters.map((c, cIdx) => (
            <div key={c.slug} id={c.slug} data-testid={`cluster-${c.slug}`}>
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-8">
                <div className="lg:col-span-5">
                  <Overline>Part {String(cIdx + 1).padStart(2, "0")}</Overline>
                  <h2 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter leading-[0.95]">
                    {c.name}
                  </h2>
                </div>
                <div className="lg:col-span-7 lg:pt-6">
                  <p className="text-white/70 text-base lg:text-lg leading-relaxed max-w-2xl">
                    {c.summary}
                  </p>
                </div>
              </div>
              <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {byCluster[c.name].map((a) => (
                  <li key={a.slug}>
                    <Link
                      to={`/brand-branding/${a.slug}`}
                      data-testid={`hub-article-${a.slug}`}
                      className="group block h-full p-6 lg:p-7 border border-white/15 hover:border-[#FFC600] transition-colors"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#FFC600]">
                          Essay {String(a.order).padStart(2, "0")}
                        </span>
                        <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-white/45">
                          {a.cluster}
                        </span>
                      </div>
                      <h3 className="text-xl lg:text-2xl font-black tracking-tighter leading-tight text-white group-hover:text-[#FFC600] transition-colors">
                        {a.title}
                      </h3>
                      <p className="mt-3 text-sm text-white/70 leading-relaxed">{a.excerpt}</p>
                      <div className="mt-5 text-sm font-semibold text-[#FFC600]">
                        Read the essay →
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <CTABlock variant="blog" />
    </main>
  );
};

export default BrandBrandingHub;
